import React, { useState, useEffect } from 'react';
import { ListPanel, ListItem } from './ListPanel';
import { useNewNG } from '../context/NewNGContext';
import { newNGApi } from '../services/newNGApi';

export const TimelinePanel: React.FC = () => {
  const { state } = useNewNG();
  const [timelineEvents, setTimelineEvents] = useState<ListItem[]>([]);
  const [, setIsLoading] = useState(true);

  // Format timestamp from aggregation timeline to display time and date in local timezone
  const formatTimelineTime = (timestamp: string): string => {
    try {
      // Parse timestamps like "2025-05-21 08:00 UTC" or "2025-07-15 12:43 UTC"
      const timeMatch = timestamp.match(/(\d{4})-(\d{2})-(\d{2}) (\d{1,2}):(\d{2}) UTC/);
      if (timeMatch) {
        const [, year, month, day, hour, minute] = timeMatch;

        // Create UTC date object
        const utcDate = new Date(`${year}-${month}-${day}T${hour.padStart(2, '0')}:${minute}:00Z`);

        // Format to local timezone
        const localTime = utcDate.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });

        const localDate = utcDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });

        return `${localTime} ${localDate}`;
      }
      return '--:-- ---, ----';
    } catch (error) {
      console.warn('Failed to parse timeline timestamp:', timestamp);
      return '--:-- ---, ----';
    }
  };

  // Load timeline events from current job's aggregation data
  useEffect(() => {
    const loadTimelineEvents = async () => {
      if (!state.currentJob?.job_id) {
        // Show empty timeline when no job is available
        const emptyEvents: ListItem[] = [
          { id: 1, time: '--:--', title: 'No timeline data available' }
        ];
        setTimelineEvents(emptyEvents);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);


        // Fetch aggregation data
        const aggregationData = await newNGApi.getJobAggregation(state.currentJob.job_id);

        // Find timeline in the correct location: news_events.timeline (main aggregated timeline)
        let timelineData = null;
        if (aggregationData?.news_events?.timeline && Array.isArray(aggregationData.news_events.timeline)) {
          timelineData = aggregationData.news_events.timeline;
        } else if (aggregationData?.timeline && Array.isArray(aggregationData.timeline)) {
          timelineData = aggregationData.timeline;
        }



        if (timelineData && timelineData.length > 0) {
          // Convert timeline entries to ListItem format
          const timelineItems: ListItem[] = timelineData.map((entry: any, index: number) => ({
            id: index + 1,
            time: formatTimelineTime(entry.timestamp),
            title: entry.summary || 'Timeline event'
          }));


          setTimelineEvents(timelineItems);
        } else {
          console.warn('No timeline data found in aggregation');
          // No fallback - just show empty timeline
          const emptyEvents: ListItem[] = [
            { id: 1, time: '--:--', title: 'No timeline data available' }
          ];
          setTimelineEvents(emptyEvents);
        }

      } catch (error) {
        console.error('Failed to load timeline events:', error);
        // Show error with --:-- format
        const errorEvents: ListItem[] = [
          { id: 1, time: '--:--', title: 'Timeline data unavailable' }
        ];
        setTimelineEvents(errorEvents);
      } finally {
        setIsLoading(false);
      }
    };

    loadTimelineEvents();
  }, [state.currentJob?.job_id]);

  return (
    <ListPanel
      title="Timeline"
      items={timelineEvents}
      showDescription={false}
    />
  );
};